import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Copy, Link2, RotateCcw, Trash2, Loader2, KeyRound, Shield,
  Mail, MailCheck, Download, Send, Users, FileText, Coins, AlertTriangle, CalendarClock, Settings, BellRing, HeartHandshake, Inbox, MessageSquare,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole, isSuperAdmin, isSubAdmin } from "@/lib/roles";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChangePasswordDialog } from "@/components/admin/ChangePasswordDialog";
import { ReportEmailDialog } from "@/components/admin/ReportEmailDialog";
import { SubUsersPanel } from "@/components/admin/SubUsersPanel";
import { ReportsPanel } from "@/components/admin/ReportsPanel";
import { CreditLedgerDialog } from "@/components/admin/CreditLedgerDialog";
import { BulkImportDialog } from "@/components/admin/BulkImportDialog";
import { BrandDialog } from "@/components/admin/BrandDialog";
import { ReminderSettingsDialog } from "@/components/admin/ReminderSettingsDialog";
import { TrackSelect, TrackBadge } from "@/components/admin/Track";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { MentorsPanel } from "@/components/admin/MentorsPanel";
import { OrdersPanel } from "@/components/admin/OrdersPanel";
import { ContactsPanel } from "@/components/admin/ContactsPanel";
import { useSite } from "@/lib/site";

const WeeklySummaryButton = () => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/weekly-summary/send-me", { base_url: window.location.origin });
      if (r.data.skipped) toast.info("Nothing to summarise yet — generate some codes first.");
      else if (r.data.sent) toast.success(`Weekly summary emailed: ${r.data.completed} finished, ${r.data.unused} codes unused`);
      else toast.error(`Could not deliver: ${r.data.errors?.[0] || "unknown error"}`, { duration: 9000 });
    } catch (e) { toast.error("Could not send summary"); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={send} disabled={busy} className="rounded-xl font-heading font-bold" data-testid="weekly-summary-me-btn">
      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-1" />} Email me my weekly summary
    </Button>
  );
};

const effectiveStatus = (c) => {
  if (c.status === "completed") return "completed";
  if (c.status === "unused" && c.expires_at && new Date(c.expires_at) < new Date()) return "expired";
  return c.status;
};

const StatusBadge = ({ status }) => {
  const map = {
    unused: { t: "Unused", c: "bg-emerald-100 text-emerald-700" },
    claimed: { t: "In progress", c: "bg-amber-100 text-amber-700" },
    completed: { t: "Completed", c: "bg-indigo-100 text-indigo-700" },
    expired: { t: "Expired", c: "bg-rose-100 text-rose-700" },
  };
  const s = map[status] || map.unused;
  return <span className={`font-heading font-bold text-xs px-2.5 py-1 rounded-full ${s.c}`}>{s.t}</span>;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const errDetail = (e, fallback) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : fallback; };

const CreditsBanner = ({ user }) => {
  const low = (user.credits ?? 0) <= 2;
  return (
    <div className={`mt-6 rounded-2xl border p-5 flex flex-wrap items-center gap-5 ${low ? "border-amber-300 bg-amber-50" : "border-indigo-200 bg-indigo-50"}`} data-testid="credits-banner">
      <div className={`w-12 h-12 rounded-xl bg-white flex items-center justify-center border ${low ? "border-amber-200" : "border-indigo-100"}`}>
        {low ? <AlertTriangle className="w-6 h-6 text-amber-600" /> : <Coins className="w-6 h-6 text-indigo-600" />}
      </div>
      <div className="flex-1 min-w-[200px]">
        <p className="font-heading font-extrabold text-2xl text-slate-900">
          <span data-testid="credits-remaining">{user.credits ?? 0}</span>
          <span className="font-body text-sm font-medium text-slate-500 ml-2">credits remaining</span>
        </p>
        <p className="font-body text-xs text-slate-500 mt-0.5">
          {low ? <span className="text-amber-800 font-semibold" data-testid="low-credit-warning">Running low — ask your administrator for a top-up. </span> : null}
          1 credit = 1 access code. Used so far: <span data-testid="credits-used">{user.credits_used ?? 0}</span>.
        </p>
      </div>
      {user.report_email ? (
        <p className="font-body text-xs text-slate-600" data-testid="report-email-display">Reports go to <span className="font-semibold">{user.report_email}</span></p>
      ) : (
        <p className="font-body text-xs text-amber-700" data-testid="report-email-missing">No report email set — you won't be notified when candidates finish.</p>
      )}
    </div>
  );
};

const Admin = () => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const superAdmin = isSuperAdmin(user);
  const subAdmin = isSubAdmin(user);
  const site = useSite();
  const [tab, setTab] = useState("codes");
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(true);
  const [count, setCount] = useState(1);
  const [label, setLabel] = useState("");
  const [expiresDays, setExpiresDays] = useState("");
  const [recipient, setRecipient] = useState("");
  const [track, setTrack] = useState("comeback");
  const [generating, setGenerating] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (!loading && !isAdminRole(user)) navigate("/");
  }, [user, loading, navigate]);

  const load = async () => {
    try { const res = await api.get("/admin/codes"); setCodes(res.data); }
    catch (e) { toast.error("Could not load codes"); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (isAdminRole(user)) load(); }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    const n = Number(count) || 1;
    if (subAdmin && n > (user.credits ?? 0)) { toast.error(`You only have ${user.credits ?? 0} credit(s) left.`); return; }
    setGenerating(true);
    try {
      await api.post("/admin/codes", {
        count: n,
        label: label.trim(),
        expires_days: expiresDays ? Number(expiresDays) : null,
        recipient_email: n === 1 ? recipient.trim() : "",
        base_url: window.location.origin,
        track,
      });
      toast.success(`Generated ${n} code(s)`);
      setLabel(""); setRecipient("");
      await load();
      if (subAdmin) await checkAuth();
    } catch (e) { toast.error(errDetail(e, "Generation failed")); }
    finally { setGenerating(false); }
  };

  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); }
    catch (e) { window.prompt("Copy:", text); }
  };

  const linkFor = (code) => `${window.location.origin}/access?code=${encodeURIComponent(code)}`;

  const sendEmail = async (c) => {
    let email = c.recipient_email;
    if (!email) {
      email = window.prompt("Send this code to which email address?", "");
      if (!email) return;
    }
    try {
      await api.post(`/admin/codes/${c.code_id}/email`, { link: linkFor(c.code), email });
      toast.success(`Emailed ${email}`);
      await load();
    } catch (e) { toast.error(errDetail(e, "Email failed")); }
  };

  const bulkEmail = async () => {
    const pending = codes.filter((c) => c.recipient_email && !c.emailed_at).length;
    if (pending === 0) { toast.info("No codes with a recipient are pending email."); return; }
    if (!window.confirm(`Email ${pending} pending code(s) to their recipients now?`)) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/admin/codes/email-all", { base_url: window.location.origin });
      toast.success(`Sent ${res.data.sent}${res.data.failed ? `, failed ${res.data.failed}` : ""}`);
      if (res.data.failed && res.data.errors?.length) toast.error(res.data.errors[0]);
      await load();
    } catch (e) { toast.error("Bulk email failed"); }
    finally { setBulkBusy(false); }
  };

  const exportCsv = () => {
    const headers = ["code", "track", "password", "access_link", "label", "recipient_email", "status", "created_by", "expires_at", "emailed_at", "created_at", "claimed_at", "completed_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const cell = (c, h) => {
      if (h === "status") return effectiveStatus(c);
      if (h === "access_link") return linkFor(c.code);
      return c[h];
    };
    const rows = codes.map((c) => headers.map((h) => esc(cell(c, h))).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `careercompass-codes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const reset = async (id) => {
    if (!window.confirm("Reset this code? The current holder will be logged out and the code becomes usable again.")) return;
    try { await api.post(`/admin/codes/${id}/reset`); toast.success("Code reset — usable again"); await load(); }
    catch (e) { toast.error("Reset failed"); }
  };
  const remove = async (c) => {
    const refundNote = c.owner_id && effectiveStatus(c) === "unused" ? " One credit will be refunded to the sub-user." : "";
    if (!window.confirm(`Delete this code permanently? This cannot be undone.${refundNote}`)) return;
    try {
      const res = await api.delete(`/admin/codes/${c.code_id}`);
      toast.success(res.data?.refunded ? "Code deleted — 1 credit refunded" : "Code deleted");
      await load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const eff = codes.map(effectiveStatus);
  const stats = {
    total: codes.length,
    unused: eff.filter((s) => s === "unused").length,
    inProgress: eff.filter((s) => s === "claimed").length,
    completed: eff.filter((s) => s === "completed").length,
    expired: eff.filter((s) => s === "expired").length,
  };
  const noCredits = subAdmin && (user?.credits ?? 0) <= 0;
  const TITLES = {
    codes: ["Access codes", superAdmin ? "Generate one-time codes, email them, set expiry and track usage across all sub-users." : "Generate one-time codes for your candidates, email them and view their reports."],
    subusers: ["Sub-users", "Create partner accounts, assign report credits and manage their access."],
    reports: ["Candidate reports", superAdmin ? "Every completed assessment across all sub-users, with score highlights." : "All your candidates who have completed the assessment, with score highlights."],
    settings: ["Settings", "Website & UPI payments, email sender, monthly digest, weekly summaries and expiry reminders."],
    mentors: ["Mentors", "Add mentors, review applications and control who appears in the public directory."],
    messages: ["Messages", "Contact form submissions — mentor applications, mentee enquiries and general questions."],
    requests: ["Payment requests", "UPI payments awaiting verification — confirm to send access codes or introduce mentees to mentors."],
  };
  const tabs = superAdmin
    ? [["codes", "Access codes", KeyRound], ["requests", "Requests", Inbox], ["mentors", "Mentors", HeartHandshake], ["messages", "Messages", MessageSquare], ["subusers", "Sub-users", Users], ["reports", "Reports", FileText], ["settings", "Settings", Settings]]
    : [["codes", "Access codes", KeyRound], ["reports", "Reports", FileText]];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <Shield className="w-5 h-5 text-primary" />
              <span className="font-body text-sm text-slate-500" data-testid="admin-role-label">{superAdmin ? "Super admin" : "Sub-user"}</span>
            </div>
            <h1 className="font-heading font-extrabold text-3xl text-slate-900">{TITLES[tab][0]}</h1>
            <p className="font-body text-slate-500 mt-1">{TITLES[tab][1]}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {subAdmin && <BrandDialog />}
            {subAdmin && <ReminderSettingsDialog />}
            {subAdmin && <WeeklySummaryButton />}
            {subAdmin && <CreditLedgerDialog />}
            {subAdmin && <ReportEmailDialog />}
            {user?.auth_type === "password" && <ChangePasswordDialog />}
            {tab === "codes" && (
              <>
                <BulkImportDialog credits={user?.credits} subAdmin={subAdmin} onDone={async () => { await load(); if (subAdmin) await checkAuth(); }} />
                <Button variant="outline" onClick={bulkEmail} disabled={bulkBusy} data-testid="bulk-email-btn"
                  className="rounded-xl font-heading font-bold">
                  {bulkBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Email all pending
                </Button>
                <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn"
                  className="rounded-xl font-heading font-bold">
                  <Download className="w-4 h-4 mr-1" /> Export CSV
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-6 inline-flex rounded-xl bg-slate-100 p-1" data-testid="admin-tabs">
          {tabs.map(([k, t, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-heading font-bold text-sm transition-colors ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              <Icon className="w-4 h-4" /> {t}
            </button>
          ))}
        </div>

        {tab === "subusers" && superAdmin ? (
          <div className="mt-6"><SubUsersPanel /></div>
        ) : tab === "mentors" && superAdmin ? (
          <div className="mt-6"><MentorsPanel currency={site.currency || "INR"} /></div>
        ) : tab === "requests" && superAdmin ? (
          <div className="mt-6"><OrdersPanel /></div>
        ) : tab === "messages" && superAdmin ? (
          <div className="mt-6"><ContactsPanel /></div>
        ) : tab === "reports" ? (
          <div className="mt-6"><ReportsPanel superAdmin={superAdmin} /></div>
        ) : tab === "settings" && superAdmin ? (
          <div className="mt-6"><SettingsPanel /></div>
        ) : (
          <>
            {subAdmin && user && <CreditsBanner user={user} />}

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
              {[["Total", stats.total], ["Unused", stats.unused], ["In progress", stats.inProgress], ["Completed", stats.completed], ["Expired", stats.expired]].map(([k, v]) => (
                <div key={k} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`stat-${k.replace(/\s+/g, '-').toLowerCase()}`}>
                  <p className="font-heading font-extrabold text-2xl text-slate-900">{v}</p>
                  <p className="font-body text-xs text-slate-500">{k}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">Generate new codes</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
                <div>
                  <Label className="font-heading font-bold text-slate-800">Assessment</Label>
                  <TrackSelect value={track} onChange={setTrack} testid="gen-track-select" />
                </div>
                <div>
                  <Label className="font-heading font-bold text-slate-800">How many</Label>
                  <Input type="number" min="1" max={subAdmin ? Math.max(1, user?.credits ?? 1) : 100} value={count} onChange={(e) => setCount(e.target.value)}
                    className="mt-1.5 rounded-xl" data-testid="gen-count-input" />
                </div>
                <div>
                  <Label className="font-heading font-bold text-slate-800">Label (optional)</Label>
                  <Input value={label} onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Cohort March" className="mt-1.5 rounded-xl" data-testid="gen-label-input" />
                </div>
                <div>
                  <Label className="font-heading font-bold text-slate-800">Expires in days</Label>
                  <Input type="number" min="1" value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)}
                    placeholder="never" className="mt-1.5 rounded-xl" data-testid="gen-expiry-input" />
                </div>
                <div>
                  <Label className="font-heading font-bold text-slate-800">Recipient email</Label>
                  <Input value={recipient} onChange={(e) => setRecipient(e.target.value)}
                    placeholder={Number(count) === 1 ? "optional" : "single code only"}
                    disabled={Number(count) !== 1}
                    className="mt-1.5 rounded-xl" data-testid="gen-recipient-input" />
                </div>
                <Button onClick={generate} disabled={generating || noCredits} data-testid="generate-codes-btn"
                  className="rounded-xl h-11 px-6 font-heading font-bold">
                  {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  Generate
                </Button>
              </div>
              <p className="font-body text-xs text-slate-400 mt-3">
                {noCredits ? <span className="text-rose-600 font-semibold" data-testid="no-credits-msg">You have no credits left. Ask your administrator to top up.</span>
                  : subAdmin ? `Each code uses 1 credit. You can generate up to ${user?.credits ?? 0} more.`
                  : "Assign a recipient email (single code) to enable emailing, or use the mail icon on any row."}
              </p>
            </div>

            <div className="mt-8">
              {busy ? (
                <p className="font-body text-slate-400">Loading…</p>
              ) : codes.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center" data-testid="no-codes">
                  <KeyRound className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="font-body text-slate-500">No codes yet. Generate your first batch above.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="font-heading text-xs uppercase tracking-wide text-slate-500">
                          <th className="px-5 py-3">Code</th>
                          <th className="px-5 py-3">Track</th>
                          <th className="px-5 py-3">Password</th>
                          <th className="px-5 py-3">Recipient</th>
                          {superAdmin && <th className="px-5 py-3">Created by</th>}
                          <th className="px-5 py-3">Status</th>
                          <th className="px-5 py-3">Emailed</th>
                          <th className="px-5 py-3">Completed</th>
                          <th className="px-5 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {codes.map((c) => (
                          <tr key={c.code_id} className="font-body text-sm" data-testid={`code-row-${c.code}`}>
                            <td className="px-5 py-3 font-mono font-semibold text-slate-900 whitespace-nowrap">{c.code}</td>
                            <td className="px-5 py-3"><TrackBadge track={c.track} /></td>
                            <td className="px-5 py-3 font-mono text-slate-700 whitespace-nowrap">{c.password}</td>
                            <td className="px-5 py-3 text-slate-500">
                              {c.recipient_email ? (
                                <span className="flex items-center gap-1.5">
                                  {c.emailed_at ? <MailCheck className="w-3.5 h-3.5 text-emerald-500" /> : <Mail className="w-3.5 h-3.5 text-slate-400" />}
                                  <span className="max-w-[160px] truncate">{c.recipient_email}</span>
                                  {c.expiry_reminded_at && <BellRing className="w-3 h-3 text-amber-500" title="Expiry reminder sent" data-testid={`reminded-${c.code}`} />}
                                </span>
                              ) : "—"}
                            </td>
                            {superAdmin && (
                              <td className="px-5 py-3 text-slate-500 max-w-[160px] truncate" data-testid={`created-by-${c.code}`}>
                                {c.owner_id ? <span className="inline-flex items-center gap-1"><Users className="w-3 h-3 text-indigo-400" />{c.created_by}</span> : <span className="text-slate-400">You</span>}
                              </td>
                            )}
                            <td className="px-5 py-3"><StatusBadge status={effectiveStatus(c)} /></td>
                            <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmtDate(c.emailed_at)}</td>
                            <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmtDate(c.completed_at)}</td>
                            <td className="px-5 py-3">
                              <div className="flex items-center justify-end gap-1.5">
                                {c.status === "completed" && c.assessment_id && (
                                  <Button size="sm" className="rounded-lg h-8 font-heading font-bold" title="View report"
                                    onClick={() => navigate(`/admin/report/${c.assessment_id}`)} data-testid={`view-report-${c.code}`}>
                                    <FileText className="w-3.5 h-3.5 mr-1" /> Report
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" className="rounded-lg h-8" title="Email this code"
                                  onClick={() => sendEmail(c)} data-testid={`email-${c.code}`}>
                                  <Mail className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-lg h-8" title="Copy link"
                                  onClick={() => copy(linkFor(c.code), "Access link copied")}
                                  data-testid={`copy-link-${c.code}`}>
                                  <Link2 className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="outline" className="rounded-lg h-8" title="Copy link + code + password"
                                  onClick={() => copy(`Link: ${linkFor(c.code)}\nCode: ${c.code}\nPassword: ${c.password}`, "Code + password copied")}
                                  data-testid={`copy-all-${c.code}`}>
                                  <Copy className="w-3.5 h-3.5" />
                                </Button>
                                {superAdmin && (
                                  <>
                                    <Button size="sm" variant="ghost" className="rounded-lg h-8 text-amber-600 hover:text-amber-700" title="Reset"
                                      onClick={() => reset(c.code_id)} data-testid={`reset-${c.code}`}>
                                      <RotateCcw className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="rounded-lg h-8 text-destructive hover:text-destructive" title="Delete"
                                      onClick={() => remove(c)} data-testid={`delete-${c.code}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Admin;
